import React, { useState } from "react";
import { Plus, Minus } from "lucide-react";
import { Overline } from "@/components/Section";
import { CtaBanner } from "@/pages/Home";
import SEOHead from "@/components/SEOHead";

// ── Section-organised FAQ data ─────────────────────────────────────────────
const FAQ_SECTIONS = [
  {
    id: "driver-search",
    title: "What Drivers Search For",
    faqs: [
      {
        q: "What are the best trucking companies to work for?",
        a: "The best trucking companies share a few things: transparent pay with no hidden deductions, late-model equipment that doesn't break down, home time that matches what they advertised, and a recruiter who actually picks up the phone after you're hired. At Hylas Companion, company drivers start at 85¢+ CPM with zero truck expenses, roll in 2020–2026 model trucks, and get a direct recruiter contact — not a call center. We're not the biggest carrier, and that's the point. Smaller means more accountable. Drivers who leave big impersonal fleets consistently tell us the difference is felt immediately. Pay figures are examples or ranges, not guarantees. Actual pay depends on the carrier and the driver's situation.",
      },
      {
        q: "What are the highest paying CDL jobs in trucking?",
        a: "The highest-paying CDL-A positions in trucking, from top to bottom, are typically: (1) Team OTR drivers — at $1 CPM each on a two-driver team, top earners gross $130,000–$175,000+ annually. (2) Lease Purchase / Owner-Operator — at 88% of gross revenue, experienced drivers net $100,000–$180,000+ depending on lanes and miles. (3) Solo OTR Company Driver — at 85¢+ CPM averaging 2,500–3,000 miles/week, that's $110,000–$132,000 gross annually. (4) Flatbed — specialized freight commands a premium over dry van on a per-mile basis. (5) Hazmat/Tanker — endorsements increase earning potential by 3–8¢ CPM at many carriers. Hylas Companion offers positions across all of these categories. Figures are examples or ranges, not guarantees. Actual pay depends on the carrier and the driver's situation.",
      },
      {
        q: "How does OTR pay compare to regional trucking pay?",
        a: "OTR (Over The Road) drivers typically earn more per mile but spend more time away from home — usually 2–3 weeks out at a time. Regional drivers earn slightly less per mile but get home more often, often weekly. At Hylas Companion, OTR company drivers start at 85¢+ CPM on 2,500–3,000 miles/week. Regional and dedicated positions may offer PPL (Per Practical Load) at 30%+ or a flat CPM rate, with the trade-off being more consistent home time. The right choice depends on your lifestyle: if maximizing gross annual income is the goal, OTR — especially team driving — wins. If weekly home time matters more, regional or dedicated is worth the modest pay difference. Figures are examples or ranges, not guarantees. Actual pay depends on the carrier and the driver's situation.",
      },
      {
        q: "What is Hylas Companion's home time policy?",
        a: "Home time at Hylas Companion depends on the program you choose. OTR company and lease drivers typically run 2–3 weeks out and return home for 2–4 days, or longer reset periods by arrangement. Regional and dedicated drivers can go home weekly in most programs. Local W2 positions are home daily. We do not overpromise home time to get you to sign — your recruiter will give you the exact schedule for the specific program before you commit. If a program doesn't match your life, we find a different one. Home time expectations should always be confirmed in writing before orientation.",
      },
      {
        q: "Do you offer a sign-on bonus?",
        a: "Hylas Companion does not offer a sign-on bonus, and we think that's actually better for drivers. Here's why: most carrier sign-on bonuses are paid out over 6–12 months and are clawed back if you leave early — they're a retention tool, not a gift. A $3,000 bonus paid over a year is $57/week. We'd rather give you a higher base CPM rate that compounds every mile you run and that you keep regardless of how long you stay. At 85¢+ CPM, every extra half-cent per mile on a 2,500-mile week is $12.50 weekly — $650 annually, every year, with no strings attached. Our referral bonus program pays $500 per driver you refer who completes orientation.",
      },
    ],
  },
  {
    faqs: [
      {
        q: "What is CPM and how does it work?",
        a: "CPM stands for Cents Per Mile. It's the most common pay structure for company drivers — you earn a set rate for every loaded mile you drive. Our company drivers start at 85¢+ CPM (or $1 CPM on team seats). On a typical 2,500-mile week at 85¢, that's $2,125 gross. No truck expenses, no insurance, no fuel costs. What you see on your settlement is close to what you keep. Figures are examples or ranges, not guarantees. Actual pay depends on the carrier and the driver's situation.",
      },
      {
        q: "What is PPL and how is it different from CPM?",
        a: "PPL stands for Per Practical Load — you earn a flat dollar amount per load based on a practical route formula, rather than counting every single mile on the GPS. This means you're compensated on the logical route regardless of exact routing. We offer 30%+ PPL on select accounts. It works especially well for dedicated and regional drivers where loads are consistent and predictable.",
      },
      {
        q: "How much can I make as an OTR company driver?",
        a: "At 85¢+ CPM averaging 2,500–3,000 miles per week, you're looking at $2,125–$2,550 gross per week — roughly $110,000–$132,000 annually. Team seats at $1 CPM can push that significantly higher. As a company driver, you have zero out-of-pocket truck or fuel expenses — that's all on us. Run our Gross Pay Calculator to model your exact numbers. Figures are examples or ranges, not guarantees. Actual pay depends on the carrier and the driver's situation.",
      },
      {
        q: "How much can I make on the Lease to Own program?",
        a: "Lease drivers run at 88% of gross freight revenue. After truck-related deductions (lease note, insurance, trailer, maintenance fund), experienced O/O-style lease drivers net $2,000–$3,500+/week depending on miles, loads, and lane. Use our Settlement Calculator to run your exact scenario before committing. Figures are examples or ranges, not guarantees. Actual pay depends on the carrier and the driver's situation.",
      },
      {
        q: "Do you hold my first paycheck?",
        a: "No. We do not hold your first paycheck. Settlements are processed weekly and paid on a consistent schedule. You'll know exactly when to expect your money from day one — no surprises.",
      },
      {
        q: "Is there escrow?",
        a: "Escrow applies only to our Lease to Own and Rent programs, not to company or W2 positions. For lease drivers, we hold a maintenance escrow of $0.10/mile to cover repairs and upkeep — this protects you from an unexpected large repair bill in any single week. Company drivers have zero escrow deductions.",
      },
      {
        q: "When and how do I get paid?",
        a: "Settlements are processed weekly. Most drivers receive funds via direct deposit. Your settlement statement will itemize every deduction so there are no mystery line items. We believe in full transparency on every settlement — you'll always know exactly what you earned and what was deducted.",
      },
    ],
  },
  {
    id: "about-program",
    title: "About the Program",
    faqs: [
      {
        q: "How does orientation work, and how long does it take?",
        a: "Orientation depends on your program type. For 1099 programs — company driver, lease, and rent — orientation is completed the same day. You arrive in the morning and are dispatched by afternoon, on the road and making money the same day. A same-day cash advance is also available for 1099 drivers at orientation. For W2 positions (local, regional hourly), orientation runs 2–4 days and pays $300. For both program types, Hylas Companion covers transportation to orientation, accommodation, and meals — you arrive and leave with nothing out of pocket. Your truck is pre-assigned based on the program you chose; you'll know the year and model before orientation day.",
      },
      {
        q: "What positions are available?",
        a: "We offer OTR (Over The Road) company driver, OTR team driver, regional, local, dedicated, Lease to Own, Rent Program, and Trainee seats. Non-CDL positions are also available — apply and tell us your license class and experience, and a recruiter will walk you through what's open right now. Position availability varies by program and region.",
      },
      {
        q: "Do you accept SAP drivers?",
        a: "SAP means you've completed a Substance Abuse Professional return-to-duty process. Our policy on SAP drivers depends on the specific program — some Hylas Companion group programs accept drivers who've completed their SAP program with a clean post-return record; others don't. Be upfront about your SAP status when you apply and we'll match you only with programs that actually accept your situation. We won't waste your time or theirs.",
      },
      {
        q: "Can I apply if I have a DUI on my record?",
        a: "Yes. We work with drivers who have a DUI on their record, provided at least 1 year has passed since the conviction or incident — this is the legal minimum under DOT regulations. If your DUI occurred within the last 12 months, you are not eligible to apply yet. Once that window passes, apply and your recruiter will match you with programs that accept your profile. Be upfront about it on your application so we don't waste anyone's time.",
      },
      {
        q: "Do you welcome female drivers?",
        a: "Absolutely. We recruit based on qualifications, not gender. Female CDL-A drivers are welcome in every position we offer — OTR, local, regional, lease, and trainee. We take the time to match every driver to a situation that fits their life, goals, and priorities. That applies equally to everyone.",
      },
      {
        q: "Do you have inside cameras?",
        a: "Camera policies vary by program. Some use forward-facing road cameras only; some use dual-facing cameras that include the cab interior. When we present a program opportunity, we'll tell you their exact camera setup upfront — no surprises. If inside cameras are a hard dealbreaker for you, let your recruiter know early and we'll prioritize programs whose policies fit.",
      },
      {
        q: "What is Lease to Own and how does it work?",
        a: "Lease to Own means you run at 88% of gross freight revenue in a late-model truck you can own outright at the end of the lease term. Weekly deductions cover the truck note, insurance, trailer lease, and a maintenance fund. What remains after deductions is yours. We use a full-payout structure — no balloon payment at the end. When the lease is done, you own the truck. Run our Settlement Calculator to see your exact weekly net before deciding.",
      },
      {
        q: "What is the Rent Program?",
        a: "The Rent Program is similar to Lease to Own in that you run at 88% revenue share, but without the path to ownership. You pay a weekly rental fee on a company truck instead of a lease note. Terms are more flexible and there's no long-term commitment. It's a strong option for experienced drivers who want the income upside of owner-operator status without a multi-year lease obligation.",
      },
      {
        q: "What is the Trainee Program?",
        a: "Our Trainee Program is open to CDL-A holders with little or no OTR experience. You'll ride with a seasoned trainer to build your road hours and log book confidence. After successfully completing the training period, you move to a solo seat. It's a legitimate path into OTR — and into the better pay that comes with it.",
      },
      {
        q: "How does home time work?",
        a: "Home time depends on the specific program. Regional and dedicated positions can offer weekly home time. OTR positions typically offer 1–3 days home per week on the road, or scheduled extended reset periods. We'll match you to a program that aligns with your home time expectations — tell your recruiter what you need upfront and we'll find the right fit.",
      },
      {
        q: "Do I need my own authority to run with you?",
        a: "No. Every position we offer — including lease and rent — operates under our authority or a Hylas Companion group partner's authority. You don't need your own MC number, DOT authority, or IFTA account to drive with us.",
      },
    ],
  },
  {
    id: "about-fleet",
    title: "About the Fleet",
    faqs: [
      {
        q: "What trucks do you offer?",
        a: "Our fleet includes Peterbilt 579 Ultraloft, Freightliner Cascadia Evolution, Kenworth T680 Next Gen, Volvo VNL 760/860, and International LT625 — all 2020–2026 model years. We'll ask for your preferences and do our best to match you with the model you want. See the full lineup on our Fleet page.",
      },
      {
        q: "What equipment comes standard in your trucks?",
        a: "Every truck includes: a 2,000W pure sine-wave inverter, 12V compressor refrigerator, APU (Auxiliary Power Unit) for idle-free heat and air conditioning, and the Opti Idle system for smart engine management. No upgrade packages, no add-on fees — all standard.",
      },
      {
        q: "Are the trucks new?",
        a: "Yes. Every unit in our fleet is a 2020–2026 late-model truck. We don't run old iron. Newer equipment means better fuel economy, fewer breakdowns, a more comfortable environment, and more miles — which means more money for you.",
      },
      {
        q: "Can I choose my truck?",
        a: "We take your preferences seriously and do our best to match you with your preferred make and model. Availability varies by program, but your recruiter will always tell you exactly what equipment is available before you commit to anything.",
      },
      {
        q: "Do you have sleeper cabs?",
        a: "Yes. All OTR positions come with sleeper cab trucks. The specific sleeper configuration (mid-roof, high-roof, flat top) varies by model. Several trucks in our fleet — like the Peterbilt 579 Ultraloft and Volvo VNL 860 — have premium large sleeper configurations designed for driver comfort on extended OTR runs.",
      },
    ],
  },
  {
    id: "about-process",
    title: "About the Process",
    faqs: [
      {
        q: "How do I apply?",
        a: "Go to hylascompanion.us/apply and fill out the online application. It takes under 3 minutes for the basic info. A recruiter will call you — usually the same business day. We don't ghost applicants. If you submitted an application and haven't heard from us within 1 business day, call us directly.",
      },
      {
        q: "How long does the application take?",
        a: "The online form takes under 3 minutes for core information. If you upload your CDL and medical certificate during the application, add another 2 minutes. We built our form to be fast and direct — no 40-page packet, no fax machine, no three-day wait for a callback.",
      },
      {
        q: "When will a recruiter contact me after I apply?",
        a: "Our goal is to reach out within 1 business day — usually the same day during business hours. When we call, we'll go over your situation, answer questions, and if there's a good match, walk through next steps. We call. We don't ghost.",
      },
      {
        q: "Do I need a CDL to apply?",
        a: "Not always. CDL-A is required for OTR, regional, lease, dedicated, and most company driver seats. However, non-CDL drivers can also apply — we have positions available that do not require a commercial license. Tell your recruiter your current license class during the first call and we'll match you to what's open. For our Trainee Program, you need a CDL-A but no prior OTR experience.",
      },
      {
        q: "What documents do I need to start?",
        a: "To begin onboarding, you'll typically need: a valid CDL, your current DOT medical certificate, proof of address (utility bill or government ID), Social Security card, employment history for the last 3 years (company names, supervisors, contact info), and a list of any violations or accidents. Some programs require additional documents — we'll walk you through the full checklist once you're matched.",
      },
      {
        q: "Do I pay anything to get started?",
        a: "Never. Drivers pay nothing — no application fees, no placement fees, no startup costs. Hylas Companion is compensated by the Hylas Companion group. If anyone tells you otherwise, that's not us.",
      },
      {
        q: "How long does it take to get placed?",
        a: "Timeline varies based on your experience, document readiness, and program availability. Drivers who have all documents ready and pass background checks typically get placed within 2–4 days (1099 programs) or 5–8 days (W2 positions). We'll give you an honest estimate after your first recruiter call.",
      },
    ],
  },
  {
    id: "about-hylas",
    title: "About Hylas Companion",
    faqs: [
      {
        q: "Who is Hylas Companion?",
        a: "Hylas Companion is a recruitment and placement company. We run the Hylas Market — a platform where partner motor carriers compete for qualified drivers. You're not bounced between agencies or handed off to strangers. One team manages your placement from the first call forward, and the driver's interests come first throughout the process.",
      },
      {
        q: "Are you a staffing agency or the actual operator?",
        a: "We are a recruitment and placement company, not a staffing agency or broker. We run the Hylas Market — a platform connecting qualified drivers with vetted partner carriers. Our recruiters are accountable for finding the right fit, and our Market means carriers compete for drivers, not the other way around. Your relationship with us starts at the first phone call and continues through placement and beyond.",
      },
      {
        q: "What partners operate under the Hylas Companion group?",
        a: "Hylas Companion works with a network of vetted partner carriers available through the Hylas Market. Your recruiter coordinates your placement and remains your point of contact throughout the process. The carriers we work with meet our standards for pay, equipment, and treatment of drivers.",
      },
      {
        q: "What states do you operate in?",
        a: "All 48 contiguous states. Key markets include Chicago and Joliet, IL; Denver, CO; Baltimore, MD; Phoenix, AZ; Oklahoma City, OK; Indianapolis, IN; Columbus, OH; Milwaukee, WI; Memphis and Nashville, TN; Atlanta, GA; Charlotte, NC; and Houston and Dallas, TX. But if you're outside these markets, apply anyway — we very likely have options.",
      },
      {
        q: "What is the Application Sharing Authorization?",
        a: "When Hylas Companion begins working with you, you agree to an exclusivity period — 45 days from your first introduction and 12 months from your hire date. This means you won't pursue the same opportunity through another channel during that window. Since everything is under the Hylas umbrella, this simply protects the time and effort both sides invest in the process.",
      },
      {
        q: "How is Hylas Companion different from other recruiters?",
        a: "We're a recruitment and placement company that built its own Market. Most recruiters pass you to whoever pays the highest referral fee. On the Hylas Market, partner carriers compete for drivers based on qualifications — your interests come first. Our recruiters are accountable for your placement and your experience, from the first call forward.",
      },
      {
        q: "Is Hylas Companion a marketplace?",
        a: "Yes — and that's exactly where we're headed. Hylas Companion is built as a platform: a marketplace where Hylas Companion group partners can access qualified drivers and experienced recruiters directly, and where drivers can find the right seat without going through a chain of middlemen. Whether you're a driver looking for your next opportunity or a company looking to scale their fleet, the Hylas platform connects both sides in one place. The full marketplace is coming — stay tuned.",
      },
      {
        q: "What does 'under the Hylas umbrella' mean for me as a driver?",
        a: "It means you're protected. You're not a transaction between two strangers. Under the Hylas umbrella, your application, your representation, and your placement all happen within the same operation. You're dealing with one team that's accountable to you from the first call to the day you're in the truck — and beyond.",
      },
    ],
  },
];

// ── GEO / Structured Data — FAQPage + WebPage speakable ──────────────────
const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "FAQPage",
      "@id": "https://hylascompanion.com/faq#faqpage",
      "url": "https://hylascompanion.com/faq",
      "name": "Truck Driver Jobs FAQ | CDL-A Questions Answered | Hylas Companion",
      "mainEntity": FAQ_SECTIONS.flatMap((s) =>
        s.faqs.map((f) => ({
          "@type": "Question",
          "name": f.q,
          "acceptedAnswer": { "@type": "Answer", "text": f.a },
        }))
      ),
    },
    {
      "@type": "WebPage",
      "@id": "https://hylascompanion.com/faq",
      "url": "https://hylascompanion.com/faq",
      "name": "Truck Driver Jobs FAQ | CDL-A Questions Answered | Hylas Companion",
      "description": "Every common CDL truck driver question answered: CPM vs PPL, OTR pay, lease to own, SAP drivers, fleet equipment, how to apply. No fees, ever.",
      "speakable": {
        "@type": "SpeakableSpecification",
        "cssSelector": ["h1", ".geo-citation"],
      },
      "inLanguage": "en-US",
    },
  ],
};

// ── GEO Citation panels — quotable stat blocks for AI engines ─────────────
const GEO_CITATIONS = {
  // After pay section (index 1)
  1: {
    stat: "$110K–$132K / year",
    label: "OTR Company Driver Annual Earnings",
    text: "Solo OTR company drivers at Hylas Companion earn between $110,000 and $132,000 gross annually at 85¢+ CPM on 2,500–3,000 miles per week. Zero truck expenses, zero insurance costs. Weekly direct-deposit settlement with fully itemized deductions. Figures are examples or ranges, not guarantees. Actual pay depends on the carrier and the driver's situation.",
    source: "Hylas Companion FAQ · hylascompanion.com/faq",
  },
  // After fleet section (index 3)
  3: {
    stat: "2020–2026 Fleet",
    label: "Hylas Companion Fleet Standard",
    text: "Every truck in the Hylas Companion fleet is a 2020–2026 late-model unit — Peterbilt 579 Ultraloft, Freightliner Cascadia Evolution, Kenworth T680 Next Gen, Volvo VNL 760/860, or International LT625. Standard on all: APU, 2,000W pure sine-wave inverter, 12V compressor refrigerator, and Opti Idle engine management.",
    source: "Hylas Companion Fleet · hylascompanion.com/fleet",
  },
  // After process section (index 4)
  4: {
    stat: "2–4 Days",
    label: "Time-to-Dispatch for 1099 Programs",
    text: "Hylas Companion places most 1099 drivers (company driver, lease, rent) on the road within 2–4 days of applying. Background check and document review: 1–2 business days. Orientation: same day, dispatched by afternoon. Transportation, hotel, and meals are covered for all programs at no cost to the driver.",
    source: "Hylas Companion Process · hylascompanion.com/how-it-works",
  },
};

function CitationPanel({ stat, label, text, source }) {
  return (
    <figure
      className="geo-citation my-6 border-l-4 border-primary"
      style={{ background: "#FAFAFA", padding: "1.25rem 1.5rem" }}
      itemScope
      itemType="https://schema.org/Quotation"
      data-testid="geo-citation-panel"
    >
      <div className="flex items-start gap-5 flex-wrap sm:flex-nowrap">
        <div className="shrink-0 min-w-[120px]">
          <p className="font-head font-black leading-none" style={{ fontSize: "clamp(1.1rem,3vw,1.5rem)", color: "#F5C842" }}>{stat}</p>
          <p className="font-head text-[8px] uppercase tracking-[0.3em] text-primary/60 mt-1">{label}</p>
        </div>
        <div>
          <blockquote itemProp="text" className="text-sm leading-relaxed text-foreground/65 italic mb-2">
            &ldquo;{text}&rdquo;
          </blockquote>
          <figcaption className="font-head text-[9px] uppercase tracking-[0.2em] text-foreground/30">
            — {source}
          </figcaption>
        </div>
      </div>
    </figure>
  );
}

export default function FAQ() {
  // open = "sectionIndex-questionIndex" | null
  const [open, setOpen] = useState(null);

  const toggle = (key) => setOpen((prev) => (prev === key ? null : key));

  return (
    <div data-testid="faq-page">
      <SEOHead
        title="Truck Driver Jobs FAQ | CDL-A Questions Answered"
        description="Answers to every common CDL truck driver question: CPM vs PPL pay, lease to own, SAP, first paycheck, cameras, fleet equipment, and how to apply. Hylas Companion — no fees, ever."
        keywords="truck driver FAQ, CDL-A requirements, CPM pay trucking, OTR home time, lease to own truck, trainee program trucking, SAP drivers, truck driver pay FAQ"
        path="/faq"
        schema={FAQ_SCHEMA}
      />

      {/* Hero */}
      <section className="bg-[#0A0A0A] pt-40 pb-20 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <Overline light>FAQ</Overline>
          <h1 className="font-head text-5xl sm:text-6xl font-bold uppercase tracking-[0.12em] text-white mt-5 leading-none">
            Straight<br /><span className="text-primary">Answers</span>
          </h1>
          <p className="mt-6 text-white/55 text-base max-w-xl">
            Every real question drivers ask us — answered honestly. No fluff.
          </p>
        </div>
      </section>

      {/* Section jump links */}
      <div className="border-b border-border bg-white sticky top-20 z-30">
        <div className="relative">
          {/* right-fade hint on mobile */}
          <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-white to-transparent z-10 md:hidden" />
          <div className="max-w-7xl mx-auto md:px-12">
            <div className="flex items-center gap-0 overflow-x-auto scroll-smooth snap-x snap-mandatory py-0 scrollbar-none">
              {FAQ_SECTIONS.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className="snap-start shrink-0 font-head text-[10px] uppercase tracking-[0.2em] text-foreground/50 hover:text-primary active:text-primary px-5 py-4 transition-colors whitespace-nowrap touch-manipulation"
                >
                  {s.title}
                </a>
              ))}
              {/* trailing spacer so last item isn't hidden behind the fade */}
              <span className="shrink-0 w-8 md:hidden" aria-hidden />
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Sections */}
      <div className="max-w-3xl mx-auto px-6 md:px-12 pb-24 md:pb-32 pt-12 space-y-16">
        {FAQ_SECTIONS.map((section, si) => (
          <React.Fragment key={section.id || `sec-${si}`}>
          <div id={section.id} className="scroll-mt-32">
            {/* Section heading */}
            <div className="flex items-center gap-4 mb-1">
              <span className="font-head text-[10px] uppercase tracking-[0.35em] text-primary">
                {section.title}
              </span>
              <div className="flex-1 h-px bg-primary/30" />
            </div>

            {/* Accordions */}
            <div className="border-t border-border mt-4">
              {section.faqs.map((f, qi) => {
                const key = `${si}-${qi}`;
                const isOpen = open === key;
                return (
                  <div
                    key={qi}
                    className="border-b border-border"
                    data-testid={`faq-${section.id}-${qi}`}
                  >
                    <button
                      onClick={() => toggle(key)}
                      data-testid={`faq-toggle-${si}-${qi}`}
                      className="w-full flex items-center justify-between gap-6 py-5 text-left group"
                    >
                      <span className="font-head text-base md:text-lg uppercase tracking-[0.08em] text-[#0A0A0A] group-hover:text-primary transition-colors leading-snug">
                        {f.q}
                      </span>
                      {isOpen
                        ? <Minus className="w-5 h-5 text-primary shrink-0" />
                        : <Plus className="w-5 h-5 shrink-0 text-foreground/40" />
                      }
                    </button>
                    {isOpen && (
                      <p className="pb-6 text-foreground/70 leading-relaxed max-w-2xl text-base">
                        {f.a}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          {GEO_CITATIONS[si] && <CitationPanel {...GEO_CITATIONS[si]} />}
          </React.Fragment>
        ))}
      </div>

      <CtaBanner />
    </div>
  );
}
